<script setup>
import { ref, watch } from 'vue'
import { Head, router } from '@inertiajs/vue3'
import {
    PhPersonSimpleRun, PhArrowRight, PhCheck, PhRepeat,
    PhGraduationCap, PhCaretRight, PhPencilSimple, PhTrash,
} from '@phosphor-icons/vue'

import Layout  from '@pages/Shared/layouts/StaffLayout.vue'
import Prompt  from '@pages/Shared/Prompt.vue'
import Table   from '@pages/Shared/ui/Table.vue'

import AddPromotionModal  from './components/AddPromotionModal.vue'
import EditPromotionModal from './components/EditPromotionModal.vue'
import ViewPromotionModal from './components/ViewPromotionModal.vue'

import { flashToast } from '@/toast.js'
import { formatDate }  from '@/helpers.js'

const props = defineProps({
    promotions: Object,
    sections:   Array,
    sessions:   Array,
    staff:      Array,
    filters:    Object,
    error:      String,
})

const sessionOptions = props.sessions.map(s => ({ label: s.name, value: s.id }))
const sectionOptions = props.sections.map(s => ({ label: s.name, value: s.id }))

const columns = [
    { key: 'index',       label: '#',                 type: 'index' },
    { key: 'date',        label: 'Date',              type: 'slot'  },
    { key: 'section',     label: 'Section',           type: 'slot'  },
    { key: 'sessions',    label: 'From → To Session', type: 'slot'  },
    { key: 'promoted_by', label: 'Promoted By',       type: 'slot'  },
    { key: 'counts',      label: 'Results',           type: 'slot'  },
    { key: 'actions',     label: '',                  type: 'slot'  },
]

const filters = ref({
    search:     props.filters?.search     ?? null,
    section:    props.filters?.section    ?? null,
    session:    props.filters?.session    ?? null,
    promotedBy: props.filters?.promotedBy ?? null,
})

watch(() => props.filters, (val) => { filters.value = { ...val } }, { deep: true })

const reload = (params = {}) => {
    const cleaned = Object.fromEntries(
        Object.entries({ ...filters.value, ...params })
            .filter(([_, v]) => v !== null && v !== undefined && v !== '')
    )
    router.get('/staff/manage-promotions', { ...cleaned, filter: 1 }, { preserveState: true, preserveScroll: true, replace: true })
}

const handleSearch = (value) => { filters.value.search = value; reload() }
const handleFilter = (newFilters) => { filters.value = { ...filters.value, ...newFilters }; reload() }

const newModal   = ref(false)
const editModal  = ref(false)
const viewModal  = ref(false)
const showPrompt = ref(false)

const editModalRef   = ref(null)
const viewModalRef   = ref(null)
const deleteTargetId = ref(null)

const openEdit   = (id) => { const p = props.promotions.data.find(p => p.id === id); if (p) editModalRef.value?.open(p) }
const openView   = (id) => { const p = props.promotions.data.find(p => p.id === id); if (p) viewModalRef.value?.open(p) }
const openDelete = (id) => { deleteTargetId.value = id; showPrompt.value = true }

const confirmDelete = () => {
    router.delete(`/staff/manage-promotions/${deleteTargetId.value}`, {
        onSuccess: (page) => { showPrompt.value = false; flashToast(page.props.flash) },
        onError:   ()     => { showPrompt.value = false; flashToast({ type: 'error', message: 'Error deleting promotion' }) },
    })
}
</script>

<template>
    <Layout>
        <Head title="Promotions" />

        <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
            <div>
                <h2 class="text-xl font-bold text-gray-800">Promotions</h2>
                <p class="text-sm text-gray-500 mt-0.5">Manage student promotions</p>
            </div>
            <button @click="newModal = true"
                class="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 text-sm font-semibold
                    text-white bg-primary hover:bg-primary-light rounded-lg transition">
                <PhPersonSimpleRun :size="20" /> New Promotion
            </button>
        </div>

        <div v-if="error"
            class="mb-6 p-4 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700 flex items-center gap-2">
            <span class="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0"></span>
            {{ error }}
        </div>

        <Table
            :data="promotions" :columns="columns" variant="staff" row-key="id"
            :actions="{ view: false, edit: false, delete: false }"
            :filters="[
                {
                    key: 'section', label: 'Section',
                    options: [{ label: 'All Sections', value: null }, ...sections.map(s => ({ label: s.name, value: s.id }))],
                    value: filters.section,
                },
                {
                    key: 'session', label: 'Session',
                    options: [{ label: 'All Sessions', value: null }, ...sessions.map(s => ({ label: s.name, value: s.id }))],
                    value: filters.session,
                },
                {
                    key: 'promotedBy', label: 'Promoter',
                    options: [{ label: 'All Staff', value: null }, ...staff.map(s => ({ label: s.name, value: s.id }))],
                    value: filters.promotedBy,
                },
            ]"
            @search="handleSearch"
            @filter="handleFilter"
        >
            <template #date="{ row }">
                <span class="text-sm text-gray-700">{{ formatDate(row.created_at) }}</span>
            </template>
            <template #section="{ row }">
                <span class="font-medium text-gray-800">{{ row.section?.name }}</span>
            </template>
            <template #sessions="{ row }">
                <div class="flex items-center gap-2 text-sm">
                    <span class="text-gray-700">{{ row.from_session?.name }}</span>
                    <PhArrowRight class="text-gray-400 shrink-0" :size="14" />
                    <span class="text-gray-700">{{ row.to_session?.name }}</span>
                </div>
            </template>
            <template #promoted_by="{ row }">
                <span class="text-sm text-gray-700">{{ row.promoted_by?.name ?? '—' }}</span>
            </template>
            <template #counts="{ row }">
                <div class="flex flex-wrap gap-1.5">
                    <span class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium
                                 bg-green-50 text-green-700 rounded-md border border-green-100">
                        <PhCheck :size="12" /> {{ row.promoted_count ?? 0 }} promoted
                    </span>
                    <span class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium
                                 bg-orange-50 text-orange-600 rounded-md border border-orange-100">
                        <PhRepeat :size="12" /> {{ row.repeated_count ?? 0 }} repeating
                    </span>
                    <span v-if="row.graduated_count"
                        class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium
                               bg-primary-50 text-primary rounded-md border border-primary-100">
                        <PhGraduationCap :size="12" /> {{ row.graduated_count }} graduated
                    </span>
                </div>
            </template>
            <template #actions="{ row }">
                <div class="flex items-center gap-1.5">
                    <button @click="openView(row.id)"
                        class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium
                               text-primary bg-primary-50 hover:bg-primary-100 rounded-md border border-primary-100 transition">
                        View <PhCaretRight :size="12" />
                    </button>
                    <button @click="openEdit(row.id)"
                        class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium
                               text-green-600 bg-green-50 hover:bg-green-100 rounded-md border border-gray-200 transition">
                        <PhPencilSimple :size="12" /> Edit
                    </button>
                    <button @click="openDelete(row.id)"
                        class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium
                               text-red-600 bg-red-50 hover:bg-red-100 rounded-md border border-red-100 transition">
                        <PhTrash :size="12" /> Delete
                    </button>
                </div>
            </template>
        </Table>

        <AddPromotionModal
            v-model="newModal"
            :sections="sections"
            :sessions="sessions"
            :session-options="sessionOptions"
            :section-options="sectionOptions"
        />

        <EditPromotionModal
            ref="editModalRef"
            v-model="editModal"
            :sections="sections"
            :session-options="sessionOptions"
        />

        <ViewPromotionModal
            ref="viewModalRef"
            v-model="viewModal"
            :sections="sections"
        />

        <Prompt
            title="promotion"
            message="This will reverse all student placements and cannot be undone."
            confirmText="Yes, Reverse & Delete"
            :isShow="showPrompt"
            @cancel="showPrompt = false"
            @delete="confirmDelete"
        />
    </Layout>
</template>